import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import chatIconImg from '../assets/serveses/ChatGPT Image Aug 20, 2026, 08_51_07 PM.png';

const API_KEY = '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';
const MODEL = 'qwen/qwen3.6-27b';

const SYSTEM_PROMPT = `You are a human customer service representative for QuBright Technologies LLP. 
Your goal is to provide excellent, friendly, and human-like support to website visitors.
RULES:
1. Always start the conversation by asking for the user's name if you don't know it yet.
2. DO NOT provide details, information, or context about anything other than QuBright Technologies LLP and its services (Software Development, AI, Web, Mobile, Cloud, EdTech products). 
3. If asked about unrelated topics, politely steer the conversation back to how QuBright can help them.
4. Keep your responses concise, conversational, and human-like. Do not sound like an AI.`;

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'system', content: SYSTEM_PROMPT },
    { role: 'assistant', content: 'Hi there! Welcome to QuBright Technologies LLP. 👋 Could I please get your name to start our conversation?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: 'user', content: inputValue.trim() };
    const newMessages = [...messages, userMessage];
    
    setMessages(newMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: MODEL,
          messages: newMessages
        })
      });

      const data = await response.json();
      
      if (data.choices && data.choices.length > 0) {
        let rawContent = data.choices[0].message.content;
        // Remove <think>...</think> blocks from the response
        const cleanContent = rawContent.replace(/<think>[\s\S]*?<\/think>/g, '').trim();
        
        const cleanMessage = {
          ...data.choices[0].message,
          content: cleanContent
        };
        
        setMessages([...newMessages, cleanMessage]);
      } else {
        setMessages([...newMessages, { role: 'assistant', content: 'I apologize, but I am having trouble connecting right now. Could you please try again in a moment?' }]);
      }
    } catch (error) {
      console.error('Error calling chat API:', error);
      setMessages([...newMessages, { role: 'assistant', content: 'Sorry, our system is currently experiencing a network issue. Please email us at qubrightoffice@gmail.com' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const displayMessages = messages.filter(m => m.role !== 'system');

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white w-80 sm:w-96 rounded-2xl shadow-2xl border border-slate-100 overflow-hidden mb-4 flex flex-col"
            style={{ maxHeight: 'calc(100vh - 120px)' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-brand-blue to-indigo-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white p-1 overflow-hidden">
                  <img src={chatIconImg} alt="QuBot Support" className="w-full h-full object-cover rounded-full" />
                </div>
                <div>
                  <h3 className="text-white font-syne font-bold text-base leading-tight">QuBot Support</h3>
                  <p className="text-blue-200 text-xs font-inter flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-400 inline-block animate-pulse"></span>
                    Online
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Chat Body */}
            <div className="flex-1 p-4 overflow-y-auto bg-slate-50 flex flex-col gap-3 h-[400px]">
              {displayMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm font-inter leading-relaxed shadow-sm ${
                      msg.role === 'user'
                        ? 'bg-brand-blue text-white rounded-tr-sm'
                        : 'bg-white text-slate-700 border border-slate-100 rounded-tl-sm'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-slate-100">
              <form onSubmit={handleSend} className="flex items-center gap-2 relative">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type a message..."
                  disabled={isLoading}
                  className="flex-1 bg-slate-100 rounded-full pl-4 pr-12 py-2.5 text-sm font-inter text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 disabled:opacity-70"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="absolute right-1 top-1 w-9 h-9 rounded-full bg-brand-blue text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
                >
                  <Send size={16} className="-ml-0.5" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button with Pulse Animation */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="relative group w-16 h-16 rounded-full bg-white shadow-xl border-4 border-white flex items-center justify-center z-50 overflow-hidden"
      >
        {/* Animated pulse ring */}
        <div className="absolute inset-0 rounded-full border-2 border-brand-orange animate-ping opacity-75" style={{ animationDuration: '2s' }}></div>
        <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-brand-blue to-brand-orange opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
        
        {/* Chat image */}
        <img 
          src={chatIconImg} 
          alt="Chat with us" 
          className="w-full h-full object-cover rounded-full relative z-10"
        />
        
        {/* Notification dot */}
        {!isOpen && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 border-2 border-white rounded-full z-20"></span>
        )}
      </motion.button>
    </div>
  );
}
